import { useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuthStore } from '../store/useAuthStore';

const HEARTBEAT_MS = 60_000; // 60s — doit rester < au cap serveur (90s)

/**
 * Mesure le temps de présence des collaborateurs.
 *
 * Ping la RPC `record_activity_heartbeat` toutes les 60s, mais UNIQUEMENT quand
 * l'onglet est visible (document.visibilityState === 'visible'). Onglet en
 * arrière-plan ou fermé → plus de heartbeat → aucun temps compté. Le serveur
 * cape chaque incrément, donc une longue inactivité n'est jamais surévaluée.
 *
 * À monter une seule fois (via <PresenceBroadcast /> dans MainLayout). Inerte
 * pour les utilisateurs non-collaborateurs (la RPC ignore les autres).
 */
export function useActivityHeartbeat(): void {
  const collaboratorId = useAuthStore((s) => s.collaborator?.id ?? null);

  useEffect(() => {
    if (!collaboratorId) return;

    function beat() {
      if (document.visibilityState !== 'visible') return;
      supabase.rpc('record_activity_heartbeat').then(({ error }) => {
        if (error) console.error('[heartbeat] record_activity_heartbeat failed:', error);
      });
    }

    // Ping immédiat à l'arrivée, puis à intervalle régulier.
    beat();
    const interval = setInterval(beat, HEARTBEAT_MS);

    // Ping aussi quand l'onglet redevient visible (reprise après mise en veille).
    function onVisible() {
      if (document.visibilityState === 'visible') beat();
    }
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [collaboratorId]);
}
